import { X } from "lucide-react";
import { forwardRef } from "react";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "@/components/catalyst/dialog";
import { Button } from "./Button";

export interface ModalProps {
	open: boolean;
	onCancel?: () => void;
	onOk?: () => void;
	title?: React.ReactNode;
	footer?: React.ReactNode | null;
	width?: number | string;
	centered?: boolean;
	confirmLoading?: boolean;
	okText?: string;
	cancelText?: string;
	children?: React.ReactNode;
	className?: string;
	overlayClassName?: string;
	closable?: boolean;
	onPointerDown?: React.PointerEventHandler<HTMLDivElement>;
	styles?: {
		body?: React.CSSProperties;
		header?: React.CSSProperties;
		footer?: React.CSSProperties;
		mask?: React.CSSProperties;
	};
}

type CatalystSize =
	| "xs"
	| "sm"
	| "md"
	| "lg"
	| "xl"
	| "2xl"
	| "3xl"
	| "4xl"
	| "5xl"
	| "6xl"
	| "7xl"
	| "screen";

function resolveSize(width: number | string): CatalystSize {
	const parsedWidth = typeof width === "number" ? width : Number.parseInt(String(width).trim(), 10);

	if (!Number.isFinite(parsedWidth)) {
		return "lg";
	}
	if (parsedWidth >= 1500) return "screen";
	if (parsedWidth >= 1400) return "7xl";
	if (parsedWidth >= 1200) return "6xl";
	if (parsedWidth >= 1000) return "5xl";
	if (parsedWidth >= 800) return "4xl";
	if (parsedWidth >= 720) return "3xl";
	if (parsedWidth >= 640) return "2xl";
	if (parsedWidth >= 560) return "xl";
	return "lg";
}

const Modal = forwardRef<HTMLDivElement, ModalProps>(
	(
		{
			open,
			onCancel,
			onOk,
			title,
			footer,
			width = 520,
			centered: _centered,
			confirmLoading = false,
			okText = "确定",
			cancelText = "取消",
			children,
			className,
			overlayClassName,
			closable = true,
			onPointerDown,
			styles: customStyles,
		},
		ref,
	) => {
		const size = resolveSize(width);

		const defaultFooter = (
			<>
				<Button onClick={onCancel}>{cancelText}</Button>
				<Button variant="primary" onClick={onOk} loading={confirmLoading}>
					{okText}
				</Button>
			</>
		);

		return (
			<Dialog
				open={open}
				overlayClassName={overlayClassName}
				onClose={() => {
					if (closable) onCancel?.();
				}}
				size={size}
				className={className}
			>
				<div ref={ref} onPointerDown={onPointerDown}>
					{title && (
						<div className="flex items-center justify-between" style={customStyles?.header}>
							<DialogTitle>{title}</DialogTitle>
							{closable && (
								<button
									type="button"
									onClick={onCancel}
									aria-label="关闭"
									className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors -mr-2"
								>
									<X className="h-5 w-5" />
								</button>
							)}
						</div>
					)}

					<DialogBody style={customStyles?.body}>{children}</DialogBody>

					{footer !== null && (
						<DialogActions style={customStyles?.footer}>
							{footer === undefined ? defaultFooter : footer}
						</DialogActions>
					)}
				</div>
			</Dialog>
		);
	},
);

Modal.displayName = "Modal";

// Confirm Modal Helper
interface ConfirmConfig {
	title?: React.ReactNode;
	content?: React.ReactNode;
	onOk?: () => void | Promise<void>;
	onCancel?: () => void;
	okText?: string;
	cancelText?: string;
	type?: "info" | "success" | "warning" | "error" | "confirm";
}

const confirm = (config: ConfirmConfig) => {
	return new Promise<boolean>((resolve) => {
		const confirmed = window.confirm(typeof config.title === "string" ? config.title : "确认");
		if (confirmed) {
			config.onOk?.();
			resolve(true);
		} else {
			config.onCancel?.();
			resolve(false);
		}
	});
};

interface ModalComponent extends React.ForwardRefExoticComponent<
	ModalProps & React.RefAttributes<HTMLDivElement>
> {
	confirm: typeof confirm;
}

const ModalWithConfirm = Modal as ModalComponent;
ModalWithConfirm.confirm = confirm;

export { ModalWithConfirm as Modal };
